import React, { useState, useRef } from 'react';
import { RxCross2 } from 'react-icons/rx';
import { FiEdit2 } from 'react-icons/fi';
import { ConfirmModal } from '../../../../../../common/ui';
import styles from './styles.module.css';

const ProductVariantsTable = ({
  rows,
  variantTableFocusIndex,
  removeVariantItem,
  onEditItem,
  productType,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const itemRefs = useRef([]);
  const [confirmRemove, setConfirmRemove] = useState({ open: false, variant: null, index: -1 });

  const focusToItem = (index) => {
    setSelectedIndex(index);
    itemRefs.current[index]?.focus();
  };

  React.useEffect(() => {
    if (variantTableFocusIndex >= 0) focusToItem(variantTableFocusIndex);
  }, [variantTableFocusIndex, rows]);

  const handleRemoveClick = (e, variant, index) => {
    e.stopPropagation();
    setConfirmRemove({ open: true, variant, index });
  };

  const handleConfirmRemove = () => {
    if (confirmRemove.variant != null && removeVariantItem) {
      removeVariantItem(confirmRemove.variant, confirmRemove.index);
    }
    setConfirmRemove({ open: false, variant: null, index: -1 });
  };

  const handleCancelRemove = () => {
    setConfirmRemove({ open: false, variant: null, index: -1 });
  };

  const list = rows ?? [];
  const isAvailable = (v) =>
    v === true || v === '1' || v === 1;
  const isSerial = (v) =>
    v === true || v === '1' || v === 1;

  return (
    <div className={styles.tableContainer}>
      <ConfirmModal
        open={confirmRemove.open}
        onClose={handleCancelRemove}
        onConfirm={handleConfirmRemove}
        title="Remove Variant"
        message={`Are you sure you want to remove "${confirmRemove.variant?.variantName ?? ''}" from the variants list?`}
        confirmLabel="Yes"
        cancelLabel="No"
      />
      <div
        className={
          productType === 'NON_STOCK'
            ? styles.tableHeaderNonStock
            : styles.tableHeaderStock
        }
      >
        <div className={styles.cellCode}>Code</div>
        <div className={styles.cellName}>Variant Name</div>
        <div className={styles.cellBarcode}>Barcode</div>
        {productType === 'NON_STOCK' && (
          <>
            <div className={styles.cellNum}>Price</div>
            <div className={styles.cellNum}>Cost</div>
            <div className={styles.cellCenter}>Availability</div>
          </>
        )}
        {productType === 'STOCK' && (
          <div className={styles.cellCenter}>Serial Check</div>
        )}
        <div className={styles.cellEdit} />
        <div className={styles.cellRemove} />
      </div>
      <div className={styles.itemContainer}>
        {list.map((item, index) => (
          <div
            key={index}
            className={
              selectedIndex === index ? styles.itemSelected : styles.item
            }
            ref={(el) => (itemRefs.current[index] = el)}
            tabIndex={0}
            onClick={() => focusToItem(index)}
          >
            <div
              className={
                productType === 'NON_STOCK'
                  ? styles.itemGridNonStock
                  : styles.itemGridStock
              }
            >
              <div className={styles.cellCode}>{item.variantCode ?? ''}</div>
              <div className={styles.cellName}>{item.variantName ?? ''}</div>
              <div className={styles.cellBarcode}>{item.variantBarcode ?? ''}</div>
              {productType === 'NON_STOCK' && (
                <>
                  <div className={styles.cellNum}>
                    {typeof item.nonStockPrice === 'number'
                      ? item.nonStockPrice.toFixed(2)
                      : item.nonStockPrice ?? '-'}
                  </div>
                  <div className={styles.cellNum}>
                    {typeof item.nonStockCost === 'number'
                      ? item.nonStockCost.toFixed(2)
                      : item.nonStockCost ?? '-'}
                  </div>
                  <div className={styles.cellCenter}>
                    {item.nonStockProductAvailability != null
                      ? (isAvailable(item.nonStockProductAvailability)
                          ? 'Available'
                          : 'Unavailable')
                      : '-'}
                  </div>
                </>
              )}
              {productType === 'STOCK' && (
                <div className={styles.cellCenter}>
                  {item.variantIsSerialCheck != null
                    ? (isSerial(item.variantIsSerialCheck) ? 'Yes' : 'No')
                    : '-'}
                </div>
              )}
              <div className={styles.cellEdit}>
                {onEditItem && (
                  <button
                    type="button"
                    className={styles.editBtn}
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditItem(item, index);
                    }}
                    aria-label="Edit variant"
                  >
                    <FiEdit2 size={16} />
                  </button>
                )}
              </div>
              <div className={styles.cellRemove}>
                <button
                  type="button"
                  className={styles.removeBtn}
                  onClick={(e) => handleRemoveClick(e, item, index)}
                  aria-label="Remove variant"
                >
                  <RxCross2 size={18} />
                </button>
              </div>
            </div>
            {item.images && item.images.length > 0 && (
              <div className={styles.imageStrip}>
                <span className={styles.imageStripLabel}>Images</span>
                <div className={styles.imageThumbnailRow}>
                  {item.images.map((img, imgIdx) => (
                    <img
                      key={imgIdx}
                      src={img.dataUrl}
                      alt={`variant-img-${imgIdx}`}
                      className={styles.imageThumbnail}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProductVariantsTable;
